import worldStates from "data/worldStates";
import cloneDeep from "lodash/cloneDeep";

const RANGED_PARAMS = ["price", "kilometers", "year"];

export const parseSearch = (query) => {
  const values = {};
  if (query) {
    Object.keys(query).forEach((key) => {
      if (query[key].length !== 0) {
        if (query[key].indexOf(",") > -1) {
          values[key] = query[key].split(",");
        } else if (query[key].indexOf("-") > -1) {
          values[key] = query[key].split("-");
        } else {
          values[key] = [query[key]];
        }
      }
    });
  }
  return values;
};

const getPathnameValues = (slug, attribute) => {
  const pathParts = slug || [];
  const indexMappings = {};
  const provincePosition = pathParts.length - 2; // province is always second last
  const yearPosition = 0; // year is always in position 0
  const hasProvince = !!worldStates.ca.includes(pathParts[provincePosition]);
  const hasYear = pathParts[yearPosition]?.match(/^\d+$/); // every char in string is a digit
  let remainingPositions = pathParts.length;
  let remainingPositionStartIndex = 0; // our relevant path items start at 0
  if (hasYear) {
    indexMappings.year = 0;

    // If there is a year, the next item starts one further and we have one less position to fill
    remainingPositions -= 1;
    remainingPositionStartIndex += 1;
  }
  if (hasProvince) {
    indexMappings.province = provincePosition;
    indexMappings.city = provincePosition + 1;

    // if there is a province, we also have a city so two less spots
    remainingPositions -= 2;
  }

  switch (remainingPositions) {
    case 1: // if only one item left it must be a make
      indexMappings.make = remainingPositionStartIndex;
      break;

    case 2: // if only two left it must be a model
      indexMappings.make = remainingPositionStartIndex;
      indexMappings.model = remainingPositionStartIndex + 1;
      break;

    default:
      break;
  }
  return [indexMappings[attribute], pathParts[indexMappings[attribute]]];
};

/**
 * @description Takes a slug and a valid attribute and returns the index that the attribute is
 * at when pathname is split by a '/'.
 *
 * @param {string} slug         Slug
 * @param {string} attribute    Attributes are year, city, province, make, model
 *
 * @returns {number} The position in the pathname that the attribute is located at
 */
export const getPathnameIndex = (slug, attribute) =>
  getPathnameValues(slug, attribute)[0];

/**
 * @description Takes a slug and a valid attribute and returns the value of the attribute in
 * the pathname.
 *
 * @param {string} slug         Slug
 * @param {string} attribute    Attributes are year, city, province, make, model
 *
 * @returns {number} The value of the attribute in the pathname
 */
export const getPathnameItem = (slug, attribute) =>
  getPathnameValues(slug, attribute)[1];

/**
 * @description Takes a slug, value, and key and updates the key in the slug with the
 * provided value. If the key does not exist, it will add the value in the slug.
 *
 * @param {string} slug       Slug
 * @param {string} key        province, city, country, make, model, or year
 * @param {string} value      Value that will be added/updated in pathname
 *
 * @returns {Array} A copy of new slug with replaced key/value pair
 */
export const addOrUpdateSlugValue = (slug, key, value) => {
  const newSlug = slug || [];
  const yearIndex = getPathnameIndex(slug, "year");
  const makeIndex = getPathnameIndex(slug, "make");
  const modelIndex = getPathnameIndex(slug, "model");
  switch (key) {
    case "make": {
      if (typeof makeIndex === "number") {
        newSlug[makeIndex] = value;
        if (modelIndex) {
          newSlug.splice(modelIndex, 1);
        }
        // add
      } else if (getPathnameItem(slug, "year")) {
        newSlug.splice(1, 0, value);
      } else {
        newSlug.splice(0, 0, value);
      }
      break;
    }
    case "model": {
      if (modelIndex) {
        newSlug[modelIndex] = value;
        // add
      } else {
        newSlug.splice(makeIndex + 1, 0, value);
      }
      break;
    }
    case "year": {
      if (getPathnameItem(slug, "year")) {
        newSlug[yearIndex] = value;
      } else {
        newSlug.splice(yearIndex, 0, value);
      }
      break;
    }
    case "province": {
      const provincePosition = getPathnameIndex(slug, "province");
      const formattedProvince = value.toLowerCase().replace(/\s+/g, "");
      if (typeof provincePosition === "number") {
        newSlug[provincePosition] = formattedProvince;
      } else {
        newSlug.push(formattedProvince);
      }
      break;
    }
    case "city": {
      const cityPosition = getPathnameIndex(slug, "city");
      if (typeof cityPosition === "number") {
        newSlug[cityPosition] = value;
      } else {
        newSlug.push(value);
      }
      break;
    }
    default: {
      break;
    }
  }
  return newSlug;
};

/**
 * @description Takes a slug, and an object of key/value pairs and updates each key in
 * the slug with the provided value.
 *
 * @param {array} slug        array of all items in slug
 * @param {object} params     Object of key/value pairs that will be added/updated in the pathname
 *
 * @returns {array} A copy of new slug with replaced key/value pair
 */
export const addOrUpdateRouterSlugValues = (slug, params) => {
  const paramEntries = Object.entries(params);
  let newSlug = slug;
  paramEntries.forEach((param) => {
    newSlug = addOrUpdateSlugValue(newSlug, param[0], param[1]);
  });
  return newSlug;
};

/**
 * @description Adds an item to the search part of the URL.
 *
 * @param {Object} query                Object containing all query params from url
 * @param {Object} payload              Object containing a key and value property
 * @param {string} payload.key          The name (eg. Lifestyle) of the inventory filter to add
 * @param {string} payload.value        The value (eg. Flex) of the inventory filter to add
 *
 * @returns {Object} A copy of new query with replaced key/value pair
 */
export const addQueryItem = (query, payload) => {
  // TODO: FIX
  const newQuery = cloneDeep(query);
  const { key, value } = payload;
  if (newQuery[key]) {
    newQuery[key] += `,${value}`;
  } else {
    newQuery[key] = value;
  }
  return newQuery;
};

/**
 * @description Removes a filter from the slug or query params.
 *
 * @param {Object} query                NextJS Query object
 * @param {Array}  query.slug           slug part of the query object
 * @param {Object} payload              Object containing a key and value property
 * @param {string} payload.key          The name (eg. Lifestyle) of the inventory filter to add
 * @param {string} payload.value        The value (eg. Flex) of the inventory filter to add
 *
 * @returns {Object} The updated query and slug with the provided inventory filter removed
 */
export const removeQueryOrSlugItem = (query, payload) => {
  const { slug } = query;
  const newQuery = cloneDeep(query);
  delete newQuery.slug;
  const { key, value } = payload;
  const newSlug = [];
  const make = getPathnameItem(slug, "make");
  const model = getPathnameItem(slug, "model");
  const year = getPathnameItem(slug, "year");
  const city = getPathnameItem(slug, "city");
  const province = getPathnameItem(slug, "province");
  if (RANGED_PARAMS.includes(key)) {
    delete newQuery[key];
  }
  if (key === "year") {
    if (make) newSlug.push(make);
    if (model) newSlug.push(model);
  } else if (key === "make") {
    if (year) newSlug.push(year);
  } else if (key === "model") {
    if (year) newSlug.push(year);
    if (make) newSlug.push(make);
  } else {
    if (year) newSlug.push(year);
    if (make) newSlug.push(make);
    if (model) newSlug.push(model);
  }

  if (province) newSlug.push(province);
  if (city) newSlug.push(city);

  if (newQuery[key]) {
    const parts = Array.isArray(newQuery[key])
      ? newQuery[key]
      : newQuery[key].split(",");
    const newParts = parts.filter((part) => {
      if (Array.isArray(value)) {
        return !value.includes(decodeURI(part));
      }
      return decodeURI(part) !== value;
    });
    if (newParts.length === 0) {
      delete newQuery[key];
    } else {
      newQuery[key] = newParts.join(",");
    }
  }
  return { newQuery, newSlug };
};

/**
 * @description Updates a query object and returns a new update object.
 * the key/value to replace it are
 * provided by payload.key and payload.value respectively.
 *
 * @param {Object} query                NextJS Query object (ex. ?bodyType='Coupe'&colour='Red')
 * @param {Array}  query.slug           slug part of the query object (ex. ['Make', 'Model'])
 * @param {Object} payload              Object containing a key and value property
 * @param {string} payload.key          The name (eg. Lifestyle) of the inventory filter to add
 * @param {string} payload.value        The value (eg. Flex) of the inventory filter to add
 *
 * @returns {Object} A copy of new query with replaced key/value pair
 */
export const replaceQueryItem = (query, payload) => {
  const newQuery = query;
  const { key, value } = payload;
  if (RANGED_PARAMS.includes(key)) {
    if (value[0] === null) {
      value[0] = "null";
    }
    if (value[1] === null) {
      value[1] = "null";
    }
    newQuery[key] = value.join("-");
  } else {
    newQuery[key] = value;
  }
  return newQuery;
};

/**
 * @description Returns slug and query with only the location parts remaining
 *
 * @param {Array}  slug      slug part of the query object (ex. ['Make', 'Model'])
 *
 * @returns {Object}        { newQuery, newSlug } new query object and new slug object
 */
export const resetInventoryFilter = (slug) => {
  const city = getPathnameItem(slug, "city");
  const province = getPathnameItem(slug, "province");
  const newSlug = [];
  const newQuery = {};
  if (city && province) {
    newSlug.push(province);
    newSlug.push(city);
  }
  return { newQuery, newSlug };
};

export const replaceSlugAndQuery = (
  router,
  newSlug,
  newQuery,
  shallow = true,
) => {
  router.replace(
    {
      pathname: "/cars/[[...slug]]",
      query: {
        ...newQuery,
        slug: newSlug,
      },
    },
    undefined,
    { shallow },
  );
};
